<template>
  <div>
    <div class="card-9-53 mx-9 my-7">
      <!-- Header Section -->
      <div class="flex justify-between items-center border-b border-gray-100 px-5 pt-2.5 pb-2.5 m-0">
        <div>
          <h2 class="text-xl roboto-regular text-gray-800">Manajemen Topic Push Notification</h2>
        </div>
        <div class="flex items-center space-x-2">
          <el-button 
            type="info" 
            icon="el-icon-refresh" 
            size="small" 
            @click="syncAllMandatoryTopics"
            class="roboto-regular">
            Sync Mandatory Topics
          </el-button>
          <el-button 
            type="success" 
            icon="el-icon-folder" 
            size="small" 
            @click="$router.push('/topic-categories')"
            class="roboto-regular">
            Kelola Kategori
          </el-button>
          <el-button 
            type="primary" 
            icon="el-icon-plus" 
            size="small" 
            @click="showCreateDialog = true"
            class="roboto-regular">
            Tambah Topic
          </el-button>
        </div>
      </div>

      <!-- Search Section -->
      <div class="flex justify-start items-center border-b border-gray-100 px-5 pt-2.5 pb-2.5 m-0">
        <div class="w-64">
          <el-input 
            class="roboto-mono-200" 
            placeholder="Cari topic..." 
            size="mini" 
            v-model="searchText"
            clearable
            @clear="handleSearch">
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-input>
        </div>
        <div class="ml-3">
          <el-select 
            v-model="filterCategory" 
            placeholder="Semua Kategori" 
            size="mini" 
            clearable
            @change="filterTopics"
            class="roboto-mono-200">
            <el-option 
              v-for="category in categories" 
              :key="category" 
              :label="category" 
              :value="category">
            </el-option>
          </el-select>
        </div>
        <div class="ml-3">
          <el-select 
            v-model="filterStatus" 
            placeholder="Semua Status" 
            size="mini" 
            clearable
            @change="filterTopics"
            class="roboto-mono-200">
            <el-option label="Aktif" :value="true"></el-option>
            <el-option label="Tidak Aktif" :value="false"></el-option>
          </el-select>
        </div>
        <div class="flex-grow">
          <div class="flex items-center justify-end space-x-4">
            <p class="text-sm roboto-mono-300 p-0 m-0">
              {{ filteredTopics.length || 0 }} 
              <span class="roboto-light">Topic</span>
            </p>
          </div>
        </div>
      </div>

      <!-- Error Display -->
      <div v-if="get_topics_error" class="mx-5 my-3">
        <el-alert
          :title="get_topics_error"
          type="error"
          show-icon
          :closable="false">
        </el-alert>
      </div>

      <!-- Loading State -->
      <div v-if="get_topics_loading" class="loading-container">
        <div class="loader"></div>
      </div>

      <!-- Data Table -->
      <div v-if="!get_topics_loading">
        <el-table 
          :data="paginatedTopics" 
          style="width: 100%"
          class="roboto-regular"
          :header-cell-style="{ backgroundColor: '#f5f7fa', color: '#606266' }">
          
          <el-table-column prop="displayName" label="Nama Topic" min-width="200">
            <template slot-scope="scope">
              <div class="roboto-regular">{{ scope.row.displayName }}</div>
              <div class="text-xs text-gray-500 roboto-mono-200 mt-1">
                {{ scope.row.topicName }}
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="description" label="Deskripsi" min-width="250">
            <template slot-scope="scope">
              <div class="text-sm roboto-light">
                {{ scope.row.description || '-' }}
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="category" label="Kategori" width="150" align="center">
            <template slot-scope="scope">
              <el-tag 
                v-if="scope.row.category"
                type="info" 
                size="small">
                {{ scope.row.category }}
              </el-tag>
              <span v-else class="text-gray-400 text-xs">-</span>
            </template>
          </el-table-column>

          <el-table-column prop="subscriberCount" label="Subscribers" width="120" align="center">
            <template slot-scope="scope">
              <el-tag type="success" size="small">
                {{ scope.row.subscriberCount || 0 }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="isPublic" label="Publik" width="100" align="center">
            <template slot-scope="scope">
              <el-tag 
                :type="scope.row.isPublic ? 'success' : 'warning'" 
                size="small">
                {{ scope.row.isPublic ? 'Ya' : 'Tidak' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="isAutoSubscribe" label="Tipe" width="120" align="center">
            <template slot-scope="scope">
              <el-tooltip 
                :content="scope.row.isAutoSubscribe ? 'Topic wajib - semua user otomatis subscribe' : 'Topic opsional - user bisa pilih subscribe/unsubscribe'"
                placement="top">
                <el-tag 
                  :type="scope.row.isAutoSubscribe ? 'danger' : 'info'" 
                  size="small">
                  {{ scope.row.isAutoSubscribe ? 'Wajib' : 'Opsional' }}
                </el-tag>
              </el-tooltip>
            </template>
          </el-table-column>

          <el-table-column prop="isActive" label="Status" width="100" align="center">
            <template slot-scope="scope">
              <el-tag 
                :type="scope.row.isActive ? 'success' : 'danger'" 
                size="small">
                {{ scope.row.isActive ? 'Aktif' : 'Nonaktif' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="Aksi" width="250" align="center" fixed="right">
            <template slot-scope="scope">
              <el-tooltip content="Lihat Subscribers" placement="top">
                <el-button 
                  size="mini" 
                  type="primary" 
                  icon="el-icon-view"
                  @click="viewSubscribers(scope.row)"
                  plain>
                </el-button>
              </el-tooltip>
              <el-tooltip content="Edit Topic" placement="top">
                <el-button 
                  size="mini" 
                  type="warning" 
                  icon="el-icon-edit"
                  @click="editTopic(scope.row)"
                  plain>
                </el-button>
              </el-tooltip>
              <el-tooltip 
                :content="scope.row.isAutoSubscribe ? 'Auto-Subscribe All Users' : 'Only for mandatory topics'"
                placement="top">
                <el-button 
                  size="mini" 
                  type="success" 
                  icon="el-icon-user-solid"
                  @click="handleAutoSubscribeAllUsers(scope.row)"
                  :disabled="!scope.row.isAutoSubscribe"
                  plain>
                </el-button>
              </el-tooltip>
              <el-tooltip 
                :content="scope.row.isActive ? 'Nonaktifkan' : 'Aktifkan'"
                placement="top">
                <el-button 
                  size="mini" 
                  :type="scope.row.isActive ? 'danger' : 'info'" 
                  :icon="scope.row.isActive ? 'el-icon-delete' : 'el-icon-refresh'"
                  @click="toggleTopicStatus(scope.row)"
                  plain>
                </el-button>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>

        <!-- Pagination -->
        <div class="flex justify-center mt-5 mb-3">
          <el-pagination
            @current-change="handlePageChange"
            :current-page="currentPage"
            :page-size="pageSize"
            layout="prev, pager, next"
            :total="filteredTopics.length">
          </el-pagination>
        </div>
      </div>
    </div>

    <!-- Create/Edit Topic Dialog -->
    <el-dialog 
      :title="dialogMode === 'create' ? 'Tambah Topic Baru' : 'Edit Topic'"
      :visible.sync="showCreateDialog"
      width="600px"
      @close="resetForm">
      <el-form 
        :model="topicForm" 
        :rules="topicRules" 
        ref="topicForm"
        label-width="120px"
        label-position="left">
        
        <el-form-item label="Nama Topic" prop="topicName">
          <el-input 
            v-model="topicForm.topicName" 
            placeholder="e.g. urgent-news"
            :disabled="dialogMode === 'edit'">
            <template slot="prepend">topic_</template>
          </el-input>
          <div class="text-xs text-gray-500 mt-1">Hanya huruf, angka, - dan _ (tanpa spasi)</div>
        </el-form-item>

        <el-form-item label="Nama Display" prop="displayName">
          <el-input 
            v-model="topicForm.displayName" 
            placeholder="e.g. Berita Penting">
          </el-input>
        </el-form-item>

        <el-form-item label="Deskripsi" prop="description">
          <el-input 
            type="textarea" 
            v-model="topicForm.description" 
            :rows="3"
            placeholder="Deskripsi topic...">
          </el-input>
        </el-form-item>

        <el-form-item label="Kategori" prop="category">
          <el-select 
            v-model="topicForm.category" 
            placeholder="Pilih atau ketik kategori baru"
            filterable
            allow-create
            style="width: 100%">
            <el-option 
              v-for="category in categories" 
              :key="category" 
              :label="category" 
              :value="category">
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Icon URL" prop="iconUrl">
          <el-input 
            v-model="topicForm.iconUrl" 
            placeholder="https://...">
          </el-input>
        </el-form-item>

        <el-form-item label="Status">
          <el-switch 
            v-model="topicForm.isActive"
            active-text="Aktif"
            inactive-text="Nonaktif">
          </el-switch>
        </el-form-item>

        <el-form-item label="Publik">
          <el-switch 
            v-model="topicForm.isPublic"
            active-text="Ya"
            inactive-text="Tidak">
          </el-switch>
          <div class="text-xs text-gray-500 mt-1">Topic publik bisa di-subscribe oleh semua user</div>
        </el-form-item>

        <el-form-item label="Topic Wajib">
          <el-switch 
            v-model="topicForm.isAutoSubscribe"
            active-text="Ya"
            inactive-text="Tidak"
            active-color="#ff4949"
            inactive-color="#409EFF">
          </el-switch>
          <div class="text-xs text-gray-500 mt-1">
            <div v-if="topicForm.isAutoSubscribe" class="text-red-600">
              <i class="el-icon-warning"></i> Semua user akan otomatis subscribe ke topic ini dan tidak bisa unsubscribe
            </div>
            <div v-else class="text-blue-600">
              <i class="el-icon-info"></i> User bisa memilih untuk subscribe atau unsubscribe dari topic ini
            </div>
          </div>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showCreateDialog = false">Batal</el-button>
        <el-button 
          type="primary" 
          @click="submitTopic"
          :loading="submitting">
          {{ dialogMode === 'create' ? 'Simpan' : 'Update' }}
        </el-button>
      </span>
    </el-dialog>

    <!-- View Subscribers Dialog -->
    <el-dialog 
      title="Daftar Subscriber"
      :visible.sync="showSubscribersDialog"
      width="800px">
      <div v-if="selectedTopic">
        <div class="mb-4 flex justify-between items-center">
          <div>
            <h3 class="text-lg roboto-regular">{{ selectedTopic.displayName }}</h3>
            <p class="text-sm text-gray-500">{{ selectedTopic.topicName }}</p>
          </div>
          <div class="text-sm text-gray-600">
            Total Subscribers: <strong>{{ subscribersPagination.totalElements || 0 }}</strong>
          </div>
        </div>

        <div v-if="subscribersLoading" class="text-center py-5">
          <i class="el-icon-loading text-2xl"></i>
        </div>

        <div v-else>
          <el-table 
            :data="subscribers" 
            style="width: 100%"
            max-height="350">
            <el-table-column prop="username" label="Username" width="180"></el-table-column>
            <el-table-column prop="fcmToken" label="FCM Token" min-width="250">
              <template slot-scope="scope">
                <div class="text-xs font-mono truncate">{{ scope.row.fcmToken }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="subscribedAt" label="Tanggal Subscribe" width="150">
              <template slot-scope="scope">
                <div class="text-xs">{{ scope.row.subscribedAt | formatDate }}</div>
              </template>
            </el-table-column>
          </el-table>

          <!-- Pagination -->
          <div class="mt-4 flex justify-center">
            <el-pagination
              @current-change="handleSubscriberPageChange"
              :current-page="subscribersPage + 1"
              :page-size="subscribersPageSize"
              layout="prev, pager, next, total"
              :total="subscribersPagination.totalElements || 0">
            </el-pagination>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'TopicManagementHomeView',
  data() {
    return {
      searchText: '',
      filterCategory: '',
      filterStatus: null,
      currentPage: 1,
      pageSize: 10,
      showCreateDialog: false,
      showSubscribersDialog: false,
      dialogMode: 'create', // 'create' or 'edit'
      submitting: false,
      subscribersLoading: false,
      subscribersPage: 0,
      subscribersPageSize: 20,
      subscribersPagination: {
        totalElements: 0,
        totalPages: 0,
        currentPage: 0,
        hasNext: false,
        hasPrevious: false
      },
      selectedTopic: null,
      subscribers: [],
      topicForm: {
        topicName: '',
        displayName: '',
        description: '',
        category: '',
        iconUrl: '',
        isActive: true,
        isPublic: true,
        isAutoSubscribe: false
      },
      topicRules: {
        topicName: [
          { required: true, message: 'Nama topic harus diisi', trigger: 'blur' },
          { min: 3, max: 100, message: 'Panjang 3-100 karakter', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9_-]+$/, message: 'Hanya huruf, angka, - dan _', trigger: 'blur' }
        ],
        displayName: [
          { required: true, message: 'Nama display harus diisi', trigger: 'blur' },
          { min: 3, max: 200, message: 'Panjang 3-200 karakter', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState({
      get_topics: state => state.topic.topics,
      get_topics_loading: state => state.topic.loading,
      get_topics_error: state => state.topic.error,
      categories: state => state.topic.categories
    }),
    filteredTopics() {
      let topics = this.get_topics || []
      
      // Filter by search
      if (this.searchText) {
        const search = this.searchText.toLowerCase()
        topics = topics.filter(topic => 
          topic.topicName.toLowerCase().includes(search) ||
          topic.displayName.toLowerCase().includes(search) ||
          (topic.description && topic.description.toLowerCase().includes(search))
        )
      }
      
      // Filter by category
      if (this.filterCategory) {
        topics = topics.filter(topic => topic.category === this.filterCategory)
      }
      
      // Filter by status
      if (this.filterStatus !== null && this.filterStatus !== '') {
        topics = topics.filter(topic => topic.isActive === this.filterStatus)
      }
      
      return topics
    },
    paginatedTopics() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredTopics.slice(start, end)
    }
  },
  filters: {
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      return date.toLocaleDateString('id-ID', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })
    }
  },
  methods: {
    ...mapActions('topic', [
      'getAllTopics',
      'createTopic',
      'updateTopic',
      'deleteTopic',
      'getTopicSubscribers',
      'getAllCategories'
    ]),
    async loadTopics() {
      try {
        await this.getAllTopics()
        await this.getAllCategories()
      } catch (error) {
        this.$message.error('Gagal memuat data topics')
      }
    },
    handleSearch() {
      this.currentPage = 1
    },
    filterTopics() {
      this.currentPage = 1
    },
    handlePageChange(page) {
      this.currentPage = page
    },
    editTopic(topic) {
      this.dialogMode = 'edit'
      this.selectedTopic = topic
      this.topicForm = {
        topicName: topic.topicName,
        displayName: topic.displayName,
        description: topic.description || '',
        category: topic.category || '',
        iconUrl: topic.iconUrl || '',
        isActive: topic.isActive,
        isPublic: topic.isPublic,
        isAutoSubscribe: topic.isAutoSubscribe || false
      }
      this.showCreateDialog = true
    },
    async viewSubscribers(topic) {
      this.selectedTopic = topic
      this.showSubscribersDialog = true
      this.subscribersPage = 0 // Reset to first page
      await this.loadSubscribers()
    },
    async loadSubscribers() {
      if (!this.selectedTopic) return
      
      this.subscribersLoading = true
      
      try {
        const response = await this.$store.dispatch('topic/getTopicSubscribers', {
          topicName: this.selectedTopic.topicName,
          page: this.subscribersPage,
          size: this.subscribersPageSize
        })
        
        if (response && response.data) {
          this.subscribers = response.data.content || []
          this.subscribersPagination = {
            totalElements: response.data.totalElements || 0,
            totalPages: response.data.totalPages || 0,
            currentPage: response.data.currentPage || 0,
            hasNext: response.data.hasNext || false,
            hasPrevious: response.data.hasPrevious || false
          }
        } else {
          this.subscribers = []
        }
      } catch (error) {
        this.$message.error('Gagal memuat daftar subscribers')
        this.subscribers = []
      } finally {
        this.subscribersLoading = false
      }
    },
    async handleSubscriberPageChange(page) {
      this.subscribersPage = page - 1 // Convert to 0-based index
      await this.loadSubscribers()
    },
    async toggleTopicStatus(topic) {
      const action = topic.isActive ? 'menonaktifkan' : 'mengaktifkan'
      
      try {
        await this.$confirm(
          `Apakah Anda yakin ingin ${action} topic "${topic.displayName}"?`,
          'Konfirmasi',
          {
            confirmButtonText: 'Ya',
            cancelButtonText: 'Batal',
            type: 'warning'
          }
        )
        
        const topicData = {
          ...topic,
          isActive: !topic.isActive
        }
        delete topicData.id
        delete topicData.createdAt
        delete topicData.createdBy
        delete topicData.subscriberCount
        
        await this.updateTopic({ topicId: topic.id, topicData })
        this.$message.success(`Topic berhasil ${topic.isActive ? 'dinonaktifkan' : 'diaktifkan'}`)
        await this.loadTopics()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(`Gagal ${action} topic`)
        }
      }
    },
    submitTopic() {
      this.$refs.topicForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        
        this.submitting = true
        try {
          if (this.dialogMode === 'create') {
            await this.createTopic(this.topicForm)
            this.$message.success('Topic berhasil dibuat')
          } else {
            await this.updateTopic({ 
              topicId: this.selectedTopic.id, 
              topicData: this.topicForm 
            })
            this.$message.success('Topic berhasil diupdate')
          }
          
          this.showCreateDialog = false
          await this.loadTopics()
        } catch (error) {
          this.$message.error(
            this.dialogMode === 'create' 
              ? 'Gagal membuat topic' 
              : 'Gagal mengupdate topic'
          )
        } finally {
          this.submitting = false
        }
      })
    },
    async syncAllMandatoryTopics() {
      try {
        await this.$confirm(
          `🔄 Apakah Anda yakin ingin sync SEMUA USER ke SEMUA TOPIC MANDATORY?<br><br>
          <strong>Proses ini akan:</strong><br>
          - Subscribe semua user ke semua topic mandatory<br>
          - Memastikan tidak ada user yang terlewat<br>
          - Berguna untuk maintenance dan sync ulang<br><br>
          <strong style="color: #f56c6c;">⚠️ Proses ini mungkin memakan waktu!</strong>`,
          'Konfirmasi Sync Mandatory Topics',
          {
            confirmButtonText: 'Ya, Sync Sekarang',
            cancelButtonText: 'Batal',
            type: 'warning',
            dangerouslyUseHTMLString: true,
            confirmButtonClass: 'el-button--danger'
          }
        )
        
        const loadingMessage = this.$message({
          message: 'Sedang memproses sync mandatory topics...',
          type: 'info',
          duration: 0,
          iconClass: 'el-icon-loading'
        })

        // Call Vuex action using this.$store.dispatch
        const response = await this.$store.dispatch('topic/syncMandatoryTopics')
        
        loadingMessage.close()

        if (response && response.data) {
          const result = response.data
          this.$notify({
            title: '✅ Sync Mandatory Topics Berhasil',
            message: `
              <div style="line-height: 1.6;">
                <strong>Total Mandatory Topics:</strong> ${result.totalMandatoryTopics}<br>
                <strong>Total User Diproses:</strong> ${result.totalUsersProcessed}<br>
                <strong>Total Subscriptions Dibuat:</strong> ${result.totalSubscriptionsCreated}<br>
                <strong>Waktu:</strong> ${new Date(result.timestamp).toLocaleString('id-ID')}
              </div>
            `,
            type: 'success',
            duration: 10000,
            dangerouslyUseHTMLString: true
          })
          
          // Reload topics to update subscriber counts
          await this.loadTopics()
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(
            error.response?.data?.message || 'Gagal melakukan sync mandatory topics'
          )
        }
      }
    },
    async handleAutoSubscribeAllUsers(topic) {
      console.log('🚀 Auto-subscribe button clicked for topic:', topic)
      
      if (!topic.isAutoSubscribe) {
        this.$message.warning('Hanya topic mandatory yang bisa auto-subscribe semua user')
        return
      }

      try {
        console.log('📝 Showing confirmation dialog...')
        await this.$confirm(
          `🚀 Apakah Anda yakin ingin auto-subscribe SEMUA USER ke topic "${topic.displayName}"?<br><br>
          <strong>Perhatian:</strong><br>
          - Semua user dengan FCM token aktif akan di-subscribe<br>
          - Proses akan berjalan di background (async)<br>
          - Topic ini adalah mandatory topic<br>
          - User tidak bisa unsubscribe dari topic ini<br><br>
          Total Subscribers Saat Ini: <strong>${topic.subscriberCount || 0}</strong>`,
          'Konfirmasi Auto-Subscribe',
          {
            confirmButtonText: 'Ya, Subscribe Semua',
            cancelButtonText: 'Batal',
            type: 'warning',
            dangerouslyUseHTMLString: true,
            confirmButtonClass: 'el-button--danger'
          }
        )
        
        console.log('✅ User confirmed, starting auto-subscribe process...')
        
        const loadingMessage = this.$message({
          message: '� Memulai proses auto-subscribe di background...',
          type: 'info',
          duration: 3000,
          iconClass: 'el-icon-loading'
        })

        console.log('📡 Calling API endpoint:', `topic/autoSubscribeAllUsers with topicName: ${topic.topicName}`)
        
        // Call Vuex action using this.$store.dispatch
        const response = await this.$store.dispatch('topic/autoSubscribeAllUsers', topic.topicName)
        
        console.log('📥 API Response:', response)
        
        loadingMessage.close()

        if (response && response.data) {
          const result = response.data
          console.log('✅ Process started! Result:', result)
          
          // Check if it's async response (status: processing)
          if (result.status === 'processing') {
            this.$notify({
              title: '🚀 Proses Dimulai',
              message: `
                <div style="line-height: 1.6;">
                  <strong>Topic:</strong> ${result.topicDisplayName}<br>
                  <strong>Status:</strong> Berjalan di background<br>
                  <strong>Current Subscribers:</strong> ${result.currentSubscriberCount || 0}<br><br>
                  <small style="color: #909399;">
                    ${result.note || 'Proses sedang berjalan. Refresh halaman untuk melihat hasil.'}
                  </small>
                </div>
              `,
              type: 'info',
              duration: 10000,
              dangerouslyUseHTMLString: true
            })
            
            // Optional: Auto-refresh after 5 seconds
            setTimeout(async () => {
              console.log('🔄 Auto-refreshing topics...')
              await this.loadTopics()
              this.$message.success('Data topics sudah di-refresh')
            }, 5000)
            
          } else {
            // Fallback untuk sync response (jika backend menggunakan method lama)
            this.$notify({
              title: '✅ Auto-Subscribe Berhasil',
              message: `
                <div style="line-height: 1.6;">
                  <strong>Topic:</strong> ${result.topicDisplayName}<br>
                  <strong>Total User Ter-subscribe:</strong> ${result.subscribedUserCount}<br>
                  <strong>Waktu:</strong> ${new Date(result.timestamp).toLocaleString('id-ID')}
                </div>
              `,
              type: 'success',
              duration: 8000,
              dangerouslyUseHTMLString: true
            })
            
            // Reload topics to update subscriber count
            console.log('🔄 Reloading topics...')
            await this.loadTopics()
          }
        } else {
          console.warn('⚠️ No data in response:', response)
        }
      } catch (error) {
        console.error('❌ Error in auto-subscribe:', error)
        
        if (error !== 'cancel') {
          const errorMsg = error.response?.data?.message || error.message || 'Gagal melakukan auto-subscribe'
          console.error('❌ Error details:', errorMsg)
          
          this.$message.error(errorMsg)
        } else {
          console.log('ℹ️ User cancelled the operation')
        }
      }
    },
    resetForm() {
      this.topicForm = {
        topicName: '',
        displayName: '',
        description: '',
        category: '',
        iconUrl: '',
        isActive: true,
        isPublic: true
      }
      this.dialogMode = 'create'
      this.selectedTopic = null
      if (this.$refs.topicForm) {
        this.$refs.topicForm.clearValidate()
      }
    }
  },
  mounted() {
    this.loadTopics()
  }
}
</script>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.card-9-53 {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
